import { create } from "zustand";

/**
 * F21 — shared pending-writes store.
 *
 * A tiny Zustand store of in-flight plant writes (schedule save, override
 * apply/clear, alarm-rule write), surfaced by the always-mounted Topbar pip
 * so an operator can see "changes pending vs applied" even after navigating
 * away from the page that issued the write — which the per-page StatusBanner /
 * AckStatusBadge cannot, since they die on unmount.
 *
 * This store does NOT invent any echo logic. The originating page's handler
 * calls markPending() when it sends, and resolvePending(id, "applied" |
 * "rejected") from the SAME success/failure condition that handler already
 * uses (StatusBanner's applied/rejected, AckStatusBadge's onAck). The store
 * only mirrors that decision.
 *
 * Self-emptying guarantees (so the pip is idle-invisible and never asserts an
 * unverified "applied"):
 *   - On markPending we arm a 60 s decay: if the originating page unmounts
 *     before the echo resolves (its poller is gone), the write is moved to a
 *     terminal "unconfirmed" state rather than left pending forever.
 *   - On resolvePending → "applied" we arm a 10 s auto-drop so an applied
 *     write self-clears from the pip shortly after confirmation.
 * Rejected / unconfirmed entries also auto-drop, but after a longer 60 s so
 * the operator has a chance to notice a failure.
 *
 * The module-level markPending()/resolvePending() API mirrors pushToast() so
 * handlers can fire-and-forget without wiring a hook.
 */

export type PendingKind = "schedule" | "override" | "alarmRule";

export type PendingStatus = "pending" | "applied" | "rejected" | "unconfirmed";

export interface PendingWrite {
  id: string;
  kind: PendingKind;
  /** Operator-facing one-line summary (e.g. the device name or mode). */
  label: string;
  /** Deep-link back to the page that can show full detail. Already carry-aware
   *  (built with carryTo by the caller). */
  href: string;
  startedAt: number;
  status: PendingStatus;
  /** Set when the write left the "pending" state. */
  resolvedAt?: number;
}

/** ms before an unresolved write is declared "unconfirmed" (page poller gone). */
const PENDING_DECAY_MS = 60_000;
/** ms an "applied" write lingers in the pip before auto-dropping. */
const APPLIED_DROP_MS = 10_000;
/** ms a "rejected"/"unconfirmed" write lingers before auto-dropping. */
const TERMINAL_DROP_MS = 60_000;

interface PendingWritesState {
  writes: PendingWrite[];
  mark(w: Omit<PendingWrite, "startedAt" | "status">): string;
  resolve(id: string, status: Exclude<PendingStatus, "pending">): void;
  drop(id: string): void;
}

// Per-id timers (decay + drop). Kept module-level, outside the store, so they
// survive re-renders and are cleared deterministically on resolve/drop.
const timers = new Map<string, number>();

function clearTimer(id: string) {
  const handle = timers.get(id);
  if (handle !== undefined) {
    window.clearTimeout(handle);
    timers.delete(id);
  }
}

export const usePendingWrites = create<PendingWritesState>((set, get) => ({
  writes: [],
  mark: (w) => {
    const entry: PendingWrite = { ...w, startedAt: Date.now(), status: "pending" };
    set((s) => ({ writes: [...s.writes.filter((x) => x.id !== w.id), entry] }));
    // Decay: if still pending after the window (the page that polls the echo
    // has likely unmounted), declare it "unconfirmed" rather than lying.
    clearTimer(w.id);
    if (typeof window !== "undefined") {
      timers.set(
        w.id,
        window.setTimeout(() => {
          const cur = get().writes.find((x) => x.id === w.id);
          if (cur && cur.status === "pending") get().resolve(w.id, "unconfirmed");
        }, PENDING_DECAY_MS),
      );
    }
    return w.id;
  },
  resolve: (id, status) => {
    clearTimer(id);
    set((s) => ({
      writes: s.writes.map((x) =>
        x.id === id ? { ...x, status, resolvedAt: Date.now() } : x,
      ),
    }));
    // Auto-drop so the pip self-empties and returns to idle-invisible.
    if (typeof window !== "undefined") {
      const delay = status === "applied" ? APPLIED_DROP_MS : TERMINAL_DROP_MS;
      timers.set(id, window.setTimeout(() => get().drop(id), delay));
    }
  },
  drop: (id) => {
    clearTimer(id);
    set((s) => ({ writes: s.writes.filter((x) => x.id !== id) }));
  },
}));

/** Module-level fire-and-forget API, mirroring pushToast(). Returns the id. */
export function markPending(w: Omit<PendingWrite, "startedAt" | "status">): string {
  return usePendingWrites.getState().mark(w);
}

export function resolvePending(id: string, status: Exclude<PendingStatus, "pending">): void {
  usePendingWrites.getState().resolve(id, status);
}
